import { surDictionnaireDeRepli } from "../utils/translate.utils";
import { inject, Pipe, PipeTransform } from "@angular/core";
import { LanguageService } from "../services/language.service";

const ARGS_KEY = "#";

/**
 * Séparateur des parties de la clé de cache. Le caractère NUL n'apparaît
 * jamais dans une clé de traduction ni dans un argument affichable : une
 * entrée sans arguments (`"a.b"`) ne peut donc JAMAIS entrer en collision avec
 * une entrée argumentée (`"a.b"` + NUL + `"count=2"` + NUL). La distinction
 * compte d'autant plus que le cache est désormais partagé par toute
 * l'application.
 */
const SEP = "\u0000";

/**
 * Cache PARTAGÉ par toutes les instances du pipe.
 *
 * Angular instancie un pipe par SITE DE BINDING **et par vue embarquée** :
 * chacun des `| trans` posés dans un `@for` produisait donc une `Map` par
 * ligne affichée, toutes remplies des mêmes chaînes (~765 sites en boucle sur
 * 6 614 usages). Une seule `Map` de module suffit : les traductions ne
 * dépendent que de la langue, pas du composant qui les demande.
 *
 * Elle est vidée dès que la langue change. En pratique un changement de langue
 * recharge la page (`LanguageService.setLanguage`), mais la garde reste utile
 * pour la fenêtre où la langue est posée sans rechargement immédiat
 * (formulaire de profil, qui recharge après l'enregistrement).
 *
 * ⚠️ Couplage à connaître : la clé de cache ne retient QUE la langue, pas
 * l'identité du dictionnaire. Les dictionnaires EN/ES/PT arrivent en chunks
 * différés et, tant qu'ils ne sont pas là, `messages()` retombe sur le
 * français. Une traduction demandée AVANT l'arrivée du chunk mettrait donc en
 * cache du français sous la langue étrangère — durablement, puisque la langue,
 * elle, ne changera plus. Ce qui l'empêche est l'`APP_INITIALIZER` qui attend
 * `LanguageService.pret` AVANT le bootstrap : aucun rendu, donc aucun
 * `transform`, n'a lieu avant que le dictionnaire soit en place. Le cache
 * étant désormais commun à toute l'application, retirer cette attente
 * n'abîmerait plus un seul site de binding mais l'application entière.
 */
const CACHE = new Map<string, string>();
let langueDuCache: string | null = null;

/**
 * Vide le cache partagé. Réservé aux TESTS : le cache étant au niveau du
 * module, il survit d'un test à l'autre et rendrait les specs dépendantes de
 * leur ordre d'exécution. En production il se vide tout seul au changement de
 * langue — et la page recharge de toute façon.
 */
export function viderCacheTraductions(): void {
  CACHE.clear();
  langueDuCache = null;
}

/**
 * Clé de cache des arguments, sans `JSON.stringify`.
 *
 * L'ancienne version sérialisait les arguments à CHAQUE appel, avant même de
 * consulter le cache : le coût était donc payé y compris sur les ~281 usages
 * argumentés déjà traduits. Les NOMS des arguments font partie de la clé (pas
 * seulement leurs valeurs) : `{ count: 5 }` et `{ jours: 5 }` ne produisent pas
 * la même traduction.
 */
function cleArgs(args: Record<string, any>): string {
  let cle = "";
  for (const nom of Object.keys(args)) {
    const valeur = args[nom];
    // La concaténation seule fait `String(valeur)` : deux objets ou tableaux
    // DISTINCTS s'écrasent alors sur `[object Object]`, et la première
    // traduction rendue gagne pour toutes les autres. Le cache étant désormais
    // GLOBAL, une telle collision ne contamine plus un site de binding mais
    // toute l'application, et définitivement — la langue ne changera plus pour
    // ce couple clé/arguments. Aucun usage non scalaire aujourd'hui, mais la
    // garde a disparu en même temps que le périmètre s'est élargi (review !66).
    //
    // Le chemin rapide reste celui des scalaires, qui sont la totalité des
    // ~281 usages argumentés : `JSON.stringify` n'est payé que par l'exception.
    cle +=
      nom +
      "=" +
      (valeur !== null && typeof valeur === "object"
        ? JSON.stringify(valeur)
        : valeur) +
      SEP;
  }
  return cle;
}

/**
 * Traduit une clé de message.
 *
 * `pure: true` (rétabli le 18/08/2026, après un passage en `pure: false` du
 * 04/03/2026). Le pipe est utilisé 6 614 fois, dont 765 dans des boucles :
 * impur, il était réévalué à chaque cycle de détection de chaque vue le
 * contenant.
 *
 * La pureté est correcte parce que l'application ne change JAMAIS de langue à
 * chaud : `LanguageService.setLanguage` recharge la page dès que la langue
 * change réellement. Ce rechargement n'est pas une commodité mais une
 * obligation d'architecture — `LOCALE_ID` est figé au bootstrap
 * (`app.module.ts`), donc sans lui les pipes `date` et `number` resteraient
 * dans l'ancienne locale. Le dernier appelant qui changeait la langue sans
 * recharger (`AuthService.loadInitData`, au login) a été corrigé en amont de
 * ce retour à `pure: true`.
 */
@Pipe({
  name: "trans",
  pure: true,
  standalone: true,
})
export class TranslatePipe implements PipeTransform {
  private languageService = inject(LanguageService);

  transform(value: any, args?: { count?: number; [key: string]: any }): string {
    if (!value) {
      return "";
    }

    const lang = this.languageService.currentLang();
    if (lang !== langueDuCache) {
      CACHE.clear();
      langueDuCache = lang;
    }

    const cacheKey = args ? `${value}${SEP}${cleArgs(args)}` : value;
    const cached = CACHE.get(cacheKey);
    if (cached !== undefined) {
      return cached;
    }

    const messages = this.languageService.messages();

    // Gestion du pluriel : si count > 1, cherche la clé _plural
    let key = value;
    if (args?.count !== undefined && args.count > 1) {
      key = `${value}_plural`;
    }

    const splitMessage: string[] = key.split(".");
    let message = this.readMessage(splitMessage, 0, messages);

    // Fallback sur la clé originale si _plural n'existe pas
    if (!message && key !== value) {
      const fallbackSplit: string[] = value.split(".");
      message = this.readMessage(fallbackSplit, 0, messages);
    }

    if (!message) {
      if (!surDictionnaireDeRepli()) CACHE.set(cacheKey, value);
      return value;
    }

    if (args) {
      const keys = Object.keys(args);
      for (const argKey of keys) {
        message = message.replace(`${ARGS_KEY}${argKey}`, String(args[argKey]));
      }
    }

    // ⚠️ ON NE CACHE PAS SUR LE DICTIONNAIRE DE REPLI. La clé de cache ne
    // retient que la LANGUE, pas l'identité du dictionnaire : cacher pendant
    // que `messages()` retombe sur le français mettrait du français sous la
    // langue étrangère, et durablement puisque la langue ne change plus à
    // chaud. C'est la collision que le commentaire de `CACHE` décrit — elle
    // était impossible tant que l'`APP_INITIALIZER` attendait le dictionnaire
    // sans limite ; le repli à 5 s l'a rendue atteignable (review !67), ce
    // signal la referme.
    if (!surDictionnaireDeRepli()) {
      CACHE.set(cacheKey, message);
    }
    return message;
  }

  private readMessage(params: string[], index: number, response: any): any {
    if (params.length === index) {
      return response;
    }

    const message = response[params[index]];
    if (!message) {
      return message;
    }

    return this.readMessage(params, index + 1, message);
  }
}
